<script>
  import { onMount } from "svelte";
  import SpotlightGuide from "@components/common/SpotlightGuide.svelte";
  import { registerViewTour } from "lib/stores/viewTourStore.js";
  import { projectListStore } from "@stores/damProjectsStore.js";
  import { PROJECT_STAGES } from "@utils/stagesAndSteps.js";
  import AnonymizationCheck from "./AnonymizationCheck.svelte";
  import UIElementAnonymizationCheck from "./UIElementAnonymizationCheck.svelte";

  let selectedReviewType = 0;
  // Set by "Go to masks/elements", the opened level then starts with its open entries.
  let showOpenEntriesOnly = false;
  let showReviewGuide = false;
  let reviewGuideStep = 0;
  let reviewGuideSteps = [];
  let reviewTourMounted = false;

  onMount(() => {
    reviewTourMounted = true;
    const unregisterTour = registerViewTour(startReviewGuide);
    return () => {
      reviewTourMounted = false;
      unregisterTour();
    };
  });

  async function startReviewGuide() {
    const idproject = new URLSearchParams(window.location.search).get("idproject");
    const completed = await projectListStore.isStepCompleted(
      idproject,
      PROJECT_STAGES.TASK_REGISTRATION.steps.CHECK_ANON,
      PROJECT_STAGES.TASK_REGISTRATION.id,
    );
    if (!reviewTourMounted) return;
    reviewGuideSteps = [
      {
        target: '.header-status',
        title: "Review anonymization before analysis",
        text: "Check the data collected during task registration for personal or sensitive information. Make sure the relevant text is anonymized correctly before continuing with analysis.",
      },
      {
        target: '.anonymization-section .level-field',
        title: "Choose what to review",
        text: "Switch between “Masks” to review window titles and “Elements” to review text from individual UI elements. Review both levels where data is available.",
      },
      {
        target: '.anonymization-section .filter-bar',
        title: "Find entries that need review",
        text: "Choose “Not Checked” under Show to focus on outstanding entries. Use the process and task filters to narrow your review, or search for specific text.",
      },
      {
        target: '.anonymization-section .row',
        title: "Check and correct anonymization",
        text: "Review each text segment and its anonymization label. Select a segment to assign a suitable category, such as NAME, EMAIL or ID. Correct an existing label or remove anonymization where it is not needed.",
      },
      {
        target: '.anonymization-section button[aria-label="Mark as checked"], .anonymization-section button[aria-label="Mark as not checked"]',
        title: "Mark an entry as checked",
        text: selectedReviewType === 0
          ? "Once you have reviewed the entire row, click the checkmark at its end. Some rows represent multiple entries; expand grouped rows where available to inspect them. Use “Mark as not checked” if an entry needs another review."
          : "Once you have reviewed the entire row, click the checkmark at its end. Use “Mark as not checked” if an entry needs another review.",
      },
      {
        target: '.anonymization-section .safe-title-toggle, .anonymization-section .safe-text-toggle',
        title: selectedReviewType === 0 ? "Propose standard titles" : "Propose standard texts",
        text: `Click the double checkmark next to an unchanged entry if it only shows the program or a standard function, for example “Settings” or “Open File”. The entry is marked as checked and proposed for the allowlist. Once an administrator approves it, the ${selectedReviewType === 0 ? "title" : "text"} is checked automatically in future projects. Click the blue double checkmark again to withdraw your proposal.`,
      },
      {
        target: '.anonymization-section .row',
        title: "Review suggestions for similar entries",
        text: "A rule preview may appear after you mark an entry as checked. Review the matching entries and keep only the ones the rule should apply to. Click “Apply selected” to confirm your selection, or “Cancel” to dismiss the suggestion.",
      },
      {
        target: '.header-status',
        title: completed ? "Anonymization check is complete" : "Review progress and complete the check",
        text: completed
          ? "This project step has been marked as complete. Return to Project Progress to review the remaining Discovery steps and continue with your project."
          : "“Checked” shows how much of the review is done, for masks and elements together. Once it reaches 100% and task registration has ended, click “Complete Anonymization Check” and confirm. Completing this step cannot be undone.",
      },
    ].filter(({ target }) => {
      const element = document.querySelector(target);
      return element && getComputedStyle(element).visibility === "visible" && element.getBoundingClientRect().width > 0 && element.getBoundingClientRect().height > 0;
    });
    reviewGuideStep = 0;
    showReviewGuide = reviewGuideSteps.length > 0;
  }
</script>

{#if selectedReviewType === 0}
  <AnonymizationCheck bind:selectedReviewType bind:showOpenEntriesOnly />
{:else}
  <UIElementAnonymizationCheck bind:selectedReviewType bind:showOpenEntriesOnly />
{/if}

<SpotlightGuide bind:open={showReviewGuide} bind:currentStep={reviewGuideStep} steps={reviewGuideSteps} allowInteraction={false} />
